import React, { useCallback, useEffect, useState } from "react";
import { api, API, brl, fmtDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download, FileSpreadsheet } from "lucide-react";

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const start30 = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(start30);
  const [end, setEnd] = useState(today);
  const [summary, setSummary] = useState(null);
  const [anac, setAnac] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: start ? `${start}T00:00:00` : undefined,
        end: end ? `${end}T23:59:59` : undefined,
      };
      const [a, b] = await Promise.all([
        api.get("/reports/summary", { params }),
        api.get("/reports/anac", { params }),
      ]);
      setSummary(a.data);
      setAnac(b.data);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportCSV = (rows, filename) => {
    if (!rows?.length) return;
    const cols = Object.keys(rows[0]);
    const csv = [
      cols.join(";"),
      ...rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(";")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportAnacPDF = () => {
    const params = new URLSearchParams();
    if (start) params.set("start", `${start}T00:00:00`);
    if (end) params.set("end", `${end}T23:59:59`);
    const url = `${API}/reports/anac.pdf?${params.toString()}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Inteligência</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-950">Relatórios & ANAC</h1>
        <p className="text-sm text-slate-600 mt-1">Análise operacional, financeira e dados regulatórios.</p>
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-sm border-slate-200 mb-6">
        <form
          onSubmit={(e) => { e.preventDefault(); fetchAll(); }}
          className="flex flex-col sm:flex-row gap-3 items-end"
        >
          <div>
            <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">Início</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 rounded-sm" data-testid="report-start-input" />
          </div>
          <div>
            <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">Fim</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 rounded-sm" data-testid="report-end-input" />
          </div>
          <Button type="submit" className="h-11 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm" data-testid="report-apply-button">
            Aplicar Filtro
          </Button>
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="outline" className="h-11 rounded-sm" onClick={() => exportCSV(summary?.by_day || [], "vendas-diarias.csv")} data-testid="export-csv-button">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV
            </Button>
          </div>
        </form>
      </Card>

      <Tabs defaultValue="bi">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-1">
          <TabsTrigger value="bi" className="rounded-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-bi">Painel BI</TabsTrigger>
          <TabsTrigger value="sales" className="rounded-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-sales">Vendas</TabsTrigger>
          <TabsTrigger value="anac" className="rounded-sm data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-anac">Relatório ANAC</TabsTrigger>
        </TabsList>

        <TabsContent value="bi" className="mt-4 space-y-6">
          {!summary && <div className="text-slate-500 text-sm">Carregando…</div>}
          {summary && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KPI label="Receita Bruta" value={brl(summary.total_revenue)} testid="kpi-revenue" />
                <KPI label="Faturadas" value={summary.count_invoiced} testid="kpi-invoiced" />
                <KPI label="Aguardando" value={summary.pending_count} testid="kpi-pending" />
                <KPI label="Em Operação" value={summary.operating_count} testid="kpi-operating" />
              </div>

              <Card className="p-5 rounded-sm border-slate-200">
                <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-3">Receita por Dia</div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={summary.by_day} margin={{ left: 0, right: 12, top: 6, bottom: 6 }}>
                      <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#475569" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                      <Tooltip formatter={(v) => brl(v)} />
                      <Line type="monotone" dataKey="total" stroke="#0052CC" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-5 rounded-sm border-slate-200">
                  <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-3">Top Serviços (Receita)</div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={summary.by_service} margin={{ left: 0, right: 6, top: 6, bottom: 6 }}>
                        <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} interval={0} angle={-15} textAnchor="end" height={48} />
                        <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                        <Tooltip formatter={(v) => brl(v)} />
                        <Bar dataKey="total" fill="#0ea5e9" radius={[2,2,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
                <Card className="p-5 rounded-sm border-slate-200">
                  <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-3">Top Clientes</div>
                  <div className="space-y-2">
                    {summary.by_client.map((c, i) => (
                      <div key={c.name} className="flex justify-between items-center py-2 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="font-mono text-xs text-slate-400 w-6">{String(i+1).padStart(2,"0")}</div>
                          <div className="text-sm text-slate-800">{c.name}</div>
                        </div>
                        <div className="font-mono text-sm font-semibold tabular-nums">{brl(c.total)}</div>
                      </div>
                    ))}
                    {summary.by_client.length === 0 && <div className="text-slate-400 text-sm">Sem dados.</div>}
                  </div>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="sales" className="mt-4">
          <SalesReportTab />
        </TabsContent>

        <TabsContent value="anac" className="mt-4">
          <Card className="rounded-sm border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div>
                <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500">Auditoria Regulatória</div>
                <div className="font-display font-bold text-base">Operações no período · {anac.length} registros</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => exportCSV(anac, `anac-${start}_${end}.csv`)} data-testid="export-anac-csv">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" onClick={exportAnacPDF} data-testid="export-anac-pdf">
                  <Download className="w-3.5 h-3.5 mr-1" /> Exportar PDF ANAC
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto ag-scroll">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="py-2 px-3 font-semibold">Prefixo</th>
                    <th className="py-2 px-3 font-semibold">Origem</th>
                    <th className="py-2 px-3 font-semibold">Destino</th>
                    <th className="py-2 px-3 font-semibold">Chegada</th>
                    <th className="py-2 px-3 font-semibold">Partida</th>
                    <th className="py-2 px-3 font-semibold">Piloto</th>
                    <th className="py-2 px-3 font-semibold">ANAC</th>
                    <th className="py-2 px-3 font-semibold text-right">Pax</th>
                    <th className="py-2 px-3 font-semibold">Permanência</th>
                  </tr>
                </thead>
                <tbody>
                  {anac.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Sem registros no período.</td></tr>}
                  {anac.map((r, i) => (
                    <tr key={r.code + i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono uppercase font-semibold">{r.tail_number}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.origin || "—"}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.destination || "—"}</td>
                      <td className="py-2 px-3 font-mono text-xs">{fmtDateTime(r.arrival_datetime)}</td>
                      <td className="py-2 px-3 font-mono text-xs">{fmtDateTime(r.departure_datetime)}</td>
                      <td className="py-2 px-3">{r.pilot_name || "—"}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.pilot_anac_code || "—"}</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums">{r.passenger_count || 0}</td>
                      <td className="py-2 px-3 font-mono text-xs font-semibold">{r.permanencia_str}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <Card className="p-4 rounded-sm border-slate-200 bg-white">
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500">{label}</div>
      <div className="font-display font-black text-3xl tabular-nums mt-1 text-slate-950" data-testid={testid}>{value}</div>
    </Card>
  );
}



function SalesReportTab() {
  const today = new Date().toISOString().slice(0, 10);
  const start7 = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const [mode, setMode] = React.useState("daily");
  const [day, setDay] = React.useState(today);
  const [start, setStart] = React.useState(start7);
  const [end, setEnd] = React.useState(today);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const effectiveRange = React.useMemo(() => {
    if (mode === "daily") {
      return { start: `${day}T00:00:00`, end: `${day}T23:59:59` };
    }
    return { start: `${start}T00:00:00`, end: `${end}T23:59:59` };
  }, [mode, day, start, end]);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/reports/sales", { params: effectiveRange });
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [effectiveRange]);

  React.useEffect(() => { fetchSales(); }, [fetchSales, mode]);

  const exportPDF = () => {
    const params = new URLSearchParams({ ...effectiveRange, mode });
    window.open(`${API}/reports/sales.pdf?${params.toString()}`, "_blank", "noopener");
  };

  const exportCSVLocal = () => {
    const rows = [
      ...(data?.by_service || []).map((s) => ({ tipo: "serviço", ...s })),
      ...(data?.by_client || []).map((s) => ({ tipo: "cliente", ...s })),
    ];
    if (!rows.length) return;
    const cols = Object.keys(rows[0]);
    const csv = [cols.join(";"), ...rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(";"))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vendas-${mode}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="rounded-sm border-slate-200 overflow-hidden">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div>
          <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500">Síntese Financeira</div>
          <div className="font-display font-bold text-base">Relatório de Vendas {mode === "daily" ? "Diário" : "por Período"}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex bg-white border border-slate-200 rounded-sm p-0.5">
            <button
              onClick={() => setMode("daily")}
              data-testid="sales-mode-daily"
              className={`px-3 h-9 text-xs font-medium uppercase tracking-wider rounded-sm ${mode === "daily" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >Diário</button>
            <button
              onClick={() => setMode("period")}
              data-testid="sales-mode-period"
              className={`px-3 h-9 text-xs font-medium uppercase tracking-wider rounded-sm ${mode === "period" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >Período</button>
          </div>
          {mode === "daily" ? (
            <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-9 rounded-sm w-44" data-testid="sales-day-input" />
          ) : (
            <>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 rounded-sm w-44" data-testid="sales-start-input" />
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 rounded-sm w-44" data-testid="sales-end-input" />
            </>
          )}
          <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={fetchSales} data-testid="sales-apply-button">Aplicar</Button>
          <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={exportCSVLocal} data-testid="export-sales-csv">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-9" onClick={exportPDF} data-testid="export-sales-pdf">
            <Download className="w-3.5 h-3.5 mr-1" /> Exportar PDF Sintético
          </Button>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {loading && <div className="text-slate-500 text-sm">Carregando…</div>}
        {!loading && data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <KPI label="Receita Bruta" value={brl(data.total_revenue)} testid="sales-kpi-revenue" />
              <KPI label="Notas Emitidas" value={data.count_invoiced} testid="sales-kpi-count" />
              <KPI label="Ticket Médio" value={brl(data.avg_ticket)} testid="sales-kpi-avg" />
            </div>

            {mode === "period" && data.by_day?.length > 0 && (
              <SectionTable
                title="Receita por Dia"
                rows={data.by_day}
                columns={[
                  { label: "Data", key: "date" },
                  { label: "Total", key: "total", align: "right", render: brl, mono: true },
                ]}
                testid="sales-by-day"
              />
            )}

            <SectionTable
              title="Top Serviços"
              rows={data.by_service?.slice(0, 15) || []}
              columns={[
                { label: "Serviço", key: "name" },
                { label: "Qtd.", key: "quantity", align: "right", render: (v) => `${Number(v || 0)}`, mono: true },
                { label: "Total", key: "total", align: "right", render: brl, mono: true },
              ]}
              testid="sales-by-service"
            />
            <SectionTable
              title="Top Clientes"
              rows={data.by_client?.slice(0, 15) || []}
              columns={[
                { label: "Cliente", key: "name" },
                { label: "OS", key: "count", align: "right", mono: true },
                { label: "Total", key: "total", align: "right", render: brl, mono: true },
              ]}
              testid="sales-by-client"
            />
            <SectionTable
              title="Top Aeronaves"
              rows={data.by_aircraft?.slice(0, 15) || []}
              columns={[
                { label: "Prefixo", key: "tail_number", mono: true, upper: true },
                { label: "OS", key: "count", align: "right", mono: true },
                { label: "Total", key: "total", align: "right", render: brl, mono: true },
              ]}
              testid="sales-by-aircraft"
            />
          </>
        )}
      </div>
    </Card>
  );
}

function SectionTable({ title, rows, columns, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-2">{title}</div>
      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              {columns.map((c) => (
                <th key={c.key} className={`py-2 px-3 font-semibold ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="py-6 text-center text-slate-400 text-xs">Sem dados no período.</td></tr>
            )}
            {rows.map((r, i) => {
              const rowKey = r.name || r.tail_number || r.date || `row-${i}`;
              return (
                <tr key={rowKey} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  {columns.map((c) => {
                    const raw = r[c.key];
                    const v = c.render ? c.render(raw) : (raw ?? "—");
                    return (
                      <td
                        key={c.key}
                        className={`py-2 px-3 ${c.align === "right" ? "text-right" : ""} ${c.mono ? "font-mono tabular-nums" : "text-slate-700"} ${c.upper ? "uppercase" : ""}`}
                      >
                        {v}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
